import React, { useCallback, useEffect, useState } from 'react';

import {
  listRatePlanMigrations,
  pricingAudit,
  executeRatePlanMigration,
  type PricingAuditRow,
  type RatePlanMigrationRow,
  type RatePlanSummary,
} from './api';
import { onlyDate, when } from './money';

/**
 * Every price list there has ever been, and what was changed when.
 *
 * This is the reference desk, not the front door. It was previously the left
 * rail of the main screen, which made "which version am I looking at" the first
 * question the screen asked -- a question the reader almost never has, since
 * the answer is nearly always "the live one". Moved here, behind a link, it
 * costs one click on the rare occasion it is wanted and nothing the rest of the
 * time.
 */
export default function RatePlanVersions({
  plans,
  onShowPrices,
  onBack,
  toast,
}: {
  plans: RatePlanSummary[];
  onShowPrices: (id: string) => void;
  onBack: () => void;
  toast: (message: string) => void;
}) {
  const [audit, setAudit] = useState<PricingAuditRow[] | null>(null);
  const [migrations, setMigrations] = useState<RatePlanMigrationRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([pricingAudit(null, 80), listRatePlanMigrations(null)]);
      setAudit(a);
      setMigrations(m);
    } catch (e) {
      toast(`Could not read the history: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <p className="lede">Every price list there has ever been</p>
          <p className="sub" style={{ margin: '4px 0 0' }}>
            A price list is never edited once it is live — that is what lets a customer stay on one.
            Changing prices always makes a new list, and the old one stays exactly as it was for
            anybody still on it.
          </p>
        </div>
        <button className="btn" onClick={onBack}>Back to prices</button>
      </div>

      <div className="matrix-scroll">
        <table className="matrix">
          <thead>
            <tr>
              <th>Price list</th><th>Currency</th><th>State</th>
              <th>Started</th>
              <th style={{ textAlign: 'right' }}>Customers on it</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id}>
                <td className="feat-name">Version {p.version}</td>
                <td>{p.currency}</td>
                <td>
                  {p.status === 'published' ? 'Live now'
                    : p.status === 'draft' ? 'Being edited'
                    : p.status === 'scheduled' ? 'Scheduled'
                    : 'Retired'}
                  {p.note ? <div className="muted" style={{ fontSize: 11 }}>{p.note}</div> : null}
                </td>
                <td>{p.status === 'published' ? onlyDate(p.published_at) : onlyDate(p.created_at)}</td>
                <td className="num" style={{ textAlign: 'right' }}>
                  {p.workspace_count || <span className="muted">none</span>}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="quiet" onClick={() => onShowPrices(p.id)}>Show prices</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {migrations.length ? (
        <div className="card" style={{ marginTop: 18 }}>
          <h2 style={{ marginTop: 0 }}>What was decided about existing customers</h2>
          <p className="sub">
            Recorded at the moment each price list went live — including the decision to leave
            everybody where they were.
          </p>
          <table className="matrix">
            <thead>
              <tr>
                <th>Change</th><th>Decision</th><th>Due</th><th>Carried out</th>
                <th style={{ textAlign: 'right' }}>Moved</th><th />
              </tr>
            </thead>
            <tbody>
              {migrations.map((m) => (
                <tr key={m.id}>
                  <td>v{m.from_version} → v{m.to_version} <span className="muted">{m.currency}</span></td>
                  <td>
                    {STRATEGY_WORDS[m.strategy] ?? m.strategy}
                    {m.notice_days != null ? <span className="muted"> · {m.notice_days} days</span> : null}
                    {m.override_reason ? (
                      <div className="muted" style={{ fontSize: 11 }}>reason given: {m.override_reason}</div>
                    ) : null}
                  </td>
                  <td>{m.scheduled_at ? when(m.scheduled_at) : <span className="muted">—</span>}</td>
                  <td>{m.executed_at ? when(m.executed_at) : <span className="muted">not yet</span>}</td>
                  <td className="num" style={{ textAlign: 'right' }}>{m.workspaces_moved ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {!m.executed_at && m.strategy !== 'never' ? (
                      <button
                        className="btn"
                        style={{ padding: '4px 8px', fontSize: 12 }}
                        onClick={async () => {
                          try {
                            const moved = await executeRatePlanMigration(m.id);
                            toast(`${moved} customer(s) moved`);
                            await load();
                          } catch (e) {
                            toast(`Refused: ${(e as Error).message}`);
                          }
                        }}
                      >
                        Do it now
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="card" style={{ marginTop: 18 }}>
        <h2 style={{ marginTop: 0 }}>Every change ever made</h2>
        <p className="sub">
          Written by the database itself as each change lands, not by the screen that made it, so
          nothing can be changed without appearing here.
        </p>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : audit && audit.length ? (
          <div className="matrix-scroll" style={{ maxHeight: 420, overflow: 'auto' }}>
            <table className="matrix">
              <thead>
                <tr><th>When</th><th>What</th><th>Which</th><th>Change</th></tr>
              </thead>
              <tbody>
                {audit.map((a, i) => (
                  <tr key={`${a.created_at}-${i}`}>
                    <td>{when(a.created_at)}</td>
                    <td>{THING_WORDS[a.table_name] ?? a.table_name.replace('pricing_rate_plan_', '')}</td>
                    <td className="feat-key">{a.record_key ?? '—'}</td>
                    <td className="muted" style={{ whiteSpace: 'normal', maxWidth: 460 }}>
                      {summarizeChange(a)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">Nothing recorded yet.</p>
        )}
      </div>
    </>
  );
}

const STRATEGY_WORDS: Record<string, string> = {
  never: 'Nobody moved',
  immediate: 'Everyone moved straight away',
  on_renewal: 'Each moved at their renewal',
  after_notice: 'Everyone moved after notice',
};

const THING_WORDS: Record<string, string> = {
  pricing_rate_plan: 'The price list itself',
  pricing_rate_plan_band: 'Asset prices',
  pricing_rate_plan_edition: 'A plan',
  pricing_rate_plan_credit_pack: 'A credit top-up',
  pricing_rate_plan_credit_cost: 'What an AI action costs',
  pricing_rate_plan_trial: 'The trial',
  pricing_public_settings: 'What the website shows',
};

/** Column names an owner would not recognise, said the way they would say them. */
const FIELD_WORDS: Record<string, string> = {
  unit_minor: 'price per asset',
  monthly_price_minor: 'monthly fee',
  annual_price_minor: 'yearly fee',
  price_minor: 'price',
  included_credits_per_asset: 'credits per asset',
  asset_rate_multiplier: 'asset rate',
  is_contact_sales: 'talk to sales',
  is_featured: 'recommended',
  is_active: 'on sale',
  trial_days: 'days free',
  trial_asset_cap: 'assets during the trial',
  free_first_assets: 'assets that are always free',
  max_assets: 'top of the step',
  min_assets: 'bottom of the step',
  credits: 'credits',
  public_key: 'shown on the website as',
  public_sort_order: 'order on the website',
  catalog_currency: 'currency the website shows',
  status: 'state',
  published_at: 'went live',
};

/** Which fields moved, rather than two walls of JSON. */
function summarizeChange(row: PricingAuditRow): string {
  const before = row.before_json ?? {};
  const after = row.after_json ?? {};
  if (row.event_type === 'insert') return 'added';
  if (row.event_type === 'delete') return 'removed';
  // Bookkeeping columns move on every write and say nothing about the price.
  const noise = new Set(['updated_at', 'created_at', 'content_hash', 'id', 'rate_plan_id']);
  const show = (v: unknown) => (v == null ? 'nothing' : typeof v === 'string' ? v : JSON.stringify(v));
  const changed = Object.keys(after)
    .filter((k) => !noise.has(k) && JSON.stringify(after[k]) !== JSON.stringify(before[k]))
    .map((k) => `${FIELD_WORDS[k] ?? k}: ${show(before[k])} → ${show(after[k])}`);
  return changed.length ? changed.join(', ') : 'nothing that affects a price';
}
